/**
 * Iter 198a · Portfolio Pulse "GTM Health" tile.
 *
 * Rolls up each brand's GTM Playbook health — ICP, positioning coverage,
 * funnel completeness — with a per-brand grade + deep-link to the brand's
 * GTM tab. Brands without a playbook are dimmed as opportunities.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Rocket, ArrowRight, Loader2, RefreshCw, TrendingUp, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

const GRADE_TONE = {
  A: "border-emerald-500 text-emerald-800 bg-emerald-50",
  B: "border-sky-500 text-sky-800 bg-sky-50",
  C: "border-amber-500 text-amber-800 bg-amber-50",
  D: "border-rose-500 text-rose-800 bg-rose-50",
  "—": "border-border text-muted-foreground bg-muted/30",
};

export default function GtmHealthTile({ wsId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/workspaces/${wsId}/gtm/health`);
      setData(r.data);
    } catch {
      setData({ rows: [], brand_count: 0, with_playbook: 0, avg_overall_pct: 0 });
    } finally { setLoading(false); }
  };
  useEffect(() => { if (wsId) load(); /* eslint-disable-next-line */ }, [wsId]);

  const rows = data?.rows || [];
  const withBook = useMemo(() => rows.filter(r => r.has_playbook), [rows]);
  const missing = useMemo(() => rows.filter(r => !r.has_playbook), [rows]);
  const shown = showAll ? rows : [...withBook, ...missing.slice(0, 3)];

  if (loading && !data) {
    return <div className="border border-border bg-card p-3 text-xs text-muted-foreground">Loading GTM health…</div>;
  }

  const avg = data?.avg_overall_pct || 0;
  const strapTone = avg >= 75 ? "bg-emerald-50 border-emerald-400"
    : avg >= 50 ? "bg-sky-50 border-sky-400"
    : avg >= 25 ? "bg-amber-50 border-amber-400"
    : "bg-rose-50 border-rose-400";

  return (
    <div className="border border-border bg-card" data-testid="gtm-health-tile">
      <div className="p-3 flex items-center gap-2 border-b border-border">
        <Rocket className="w-4 h-4 text-primary" />
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
          GTM Health — playbook coverage across brands
        </div>
        <div className="ml-auto flex items-center gap-1">
          <Button size="sm" variant="ghost" className="rounded-none h-7 px-2"
                    onClick={load} disabled={loading}
                    data-testid="gtm-health-refresh">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 p-3">
        <div className={`border p-2 ${strapTone}`} data-testid="gtm-health-avg">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Avg playbook grade</div>
          <div className="text-2xl font-mono font-black flex items-baseline gap-1">
            {avg}%
            <TrendingUp className="w-4 h-4 text-muted-foreground" />
          </div>
          <div className="text-[10px] text-muted-foreground">across {data?.with_playbook || 0} active playbooks</div>
        </div>
        <div className="border border-border bg-muted/20 p-2" data-testid="gtm-health-with">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">With playbook</div>
          <div className="text-2xl font-mono font-black">{data?.with_playbook || 0}</div>
          <div className="text-[10px] text-muted-foreground">of {data?.brand_count || 0} brands</div>
        </div>
        <div className="border border-border bg-muted/20 p-2" data-testid="gtm-health-missing">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Without playbook</div>
          <div className="text-2xl font-mono font-black">{missing.length}</div>
          <div className="text-[10px] text-muted-foreground inline-flex items-center gap-0.5">
            <AlertCircle className="w-3 h-3" /> opportunity
          </div>
        </div>
      </div>
      {shown.length > 0 && (
        <div className="border-t border-border max-h-72 overflow-y-auto divide-y divide-border">
          {shown.map(b => (
            <BrandHealthRow key={b.brand_id} b={b} />
          ))}
        </div>
      )}
      {rows.length > shown.length && (
        <div className="border-t border-border p-2 text-center">
          <button type="button"
                     onClick={() => setShowAll(v => !v)}
                     className="text-[10px] uppercase tracking-widest font-semibold text-primary hover:underline"
                     data-testid="gtm-health-toggle">
            {showAll ? "Show top brands only" : `Show all ${rows.length} brands`}
          </button>
        </div>
      )}
      {shown.length === 0 && (
        <div className="text-xs text-muted-foreground italic p-4 text-center" data-testid="gtm-health-empty">
          No brands yet.
        </div>
      )}
    </div>
  );
}

function BrandHealthRow({ b }) {
  const tone = GRADE_TONE[b.grade] || GRADE_TONE["—"];
  const link = b.has_playbook
    ? `/app/brands/${b.brand_id}?tab=gtm`
    : `/app/brands/${b.brand_id}?tab=gtm&new=1`;
  return (
    <Link to={link}
             className="flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-muted/30"
             data-testid={`gtm-health-row-${b.brand_id}`}>
      <div className={`w-7 h-7 border ${tone} font-bold text-sm flex items-center justify-center`}
              data-testid={`gtm-health-grade-${b.brand_id}`}>
        {b.grade}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold truncate">{b.brand_name}</div>
        <div className="text-[10px] text-muted-foreground truncate">
          {b.has_playbook
            ? `${b.playbook_name} · ${b.playbook_status}`
            : "No GTM playbook yet — start one"}
        </div>
      </div>
      {b.has_playbook && (
        <div className="hidden md:flex items-center gap-1 shrink-0">
          <Chip label="ICP" pct={b.icp_pct} />
          <Chip label="POS" pct={b.positioning_pct} />
          <Chip label="FUN" pct={b.funnel_pct} />
        </div>
      )}
      <div className="text-sm font-mono font-black shrink-0 w-10 text-right">
        {b.has_playbook ? `${b.overall_pct}%` : "—"}
      </div>
      <ArrowRight className="w-3 h-3 text-muted-foreground shrink-0" />
    </Link>
  );
}

function Chip({ label, pct }) {
  const tone = pct >= 75 ? "border-emerald-400 text-emerald-800 bg-emerald-50"
    : pct >= 40 ? "border-amber-400 text-amber-800 bg-amber-50"
    : "border-rose-400 text-rose-800 bg-rose-50";
  return (
    <span className={`text-[9px] uppercase tracking-widest font-semibold border px-1 ${tone}`}>
      {label} {pct}
    </span>
  );
}
